import {
  FC,
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import { ReactComponent as IconEye } from "@common_assets/svg/eye-open.svg"
import { ReactComponent as IconThreeDots } from "@common_assets/svg/threeDots.svg"
import {
  Box,
  Checkbox,
  Chip,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material"
import FabIconButton from "components/common/button/fabIconButton"
import { useTranslation } from "react-i18next"
import { IGroup } from "ts/interfaces/Group"
import { groupScopeChipSx } from "utils/groupScope"

const CARD_HEIGHT = "380px"
const CARD_HEIGHT_WITH_CTA = "420px"

interface IProps {
  group: IGroup
  onCardClick: () => void
  isSelected?: boolean
  onToggleSelect?: () => void
  onEdit?: () => void
  onDelete?: () => void
  readOnly?: boolean
  onViewMembers?: () => void
  disableCardClick?: boolean
  hideCheckbox?: boolean
  hideEditAction?: boolean
  disableSchoolWideActions?: boolean
  selectMode?: boolean
  showSendTransactionCta?: boolean
}

const GroupCard: FC<IProps> = ({
  group,
  isSelected,
  onToggleSelect,
  onCardClick,
  onEdit,
  onDelete,
  readOnly = false,
  onViewMembers,
  disableCardClick = false,
  hideEditAction = false,
  disableSchoolWideActions = false,
  hideCheckbox = false,
  selectMode = false,
  showSendTransactionCta = false,
}) => {
  const { t } = useTranslation()
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(menuAnchor)

  const nameRef = useRef<HTMLElement | null>(null)
  const [isNameTruncated, setIsNameTruncated] = useState(false)

  const checkNameOverflow = useCallback(() => {
    if (nameRef.current) {
      setIsNameTruncated(
        nameRef.current.scrollWidth > nameRef.current.clientWidth,
      )
    }
  }, [])

  useEffect(() => {
    checkNameOverflow()
    document.fonts?.ready.then(checkNameOverflow)

    const resizeObserver = new ResizeObserver(checkNameOverflow)
    if (nameRef.current) resizeObserver.observe(nameRef.current)
    return () => resizeObserver.disconnect()
  }, [checkNameOverflow, group.name])

  const isSchoolWide = group.scope === "SCHOOL" && disableSchoolWideActions
  const isEmpty = group.members_count === 0
  const isClickable = selectMode ? !isSchoolWide : !isEmpty && !disableCardClick
  const isSelectedInSelectMode = selectMode && !!isSelected
  const previewMembers = group.members_preview
  const remaining = group.members_count - previewMembers.length

  const closeMenu = () => setMenuAnchor(null)
  const isCtaVisible = showSendTransactionCta && isClickable && !selectMode

  const stop = (e: MouseEvent) => e.stopPropagation()

  const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return
    if (e.key !== "Enter" && e.key !== " ") return
    e.preventDefault()
    onCardClick()
  }

  const schoolWideTooltip = isSchoolWide
    ? t("group.schoolWideGroupActionsDisabled")
    : ""
  const isCardTooltip = selectMode && isSchoolWide

  return (
    <Tooltip title={isCardTooltip ? schoolWideTooltip : ""} followCursor>
      <Stack
        onClick={isClickable ? onCardClick : undefined}
        position="relative"
        height={showSendTransactionCta ? CARD_HEIGHT_WITH_CTA : CARD_HEIGHT}
        flexShrink={0}
        gap="16px"
        px="16px"
        py="24px"
        overflow="hidden"
        minWidth={0}
        border={(theme) =>
          `1px solid ${
            isSelectedInSelectMode
              ? theme.palette.primary.main
              : theme.palette.mockup.primary95
          }`
        }
        borderRadius="16px"
        bgcolor={(theme) => theme.palette.common.white}
        sx={{
          cursor: isClickable ? "pointer" : "default",
          transition: "border-color 0.15s",
          ...(isSelectedInSelectMode && {
            boxShadow: (theme) =>
              `inset 0 0 0 1px ${theme.palette.primary.main}`,
          }),
          ...(isClickable && {
            "&:hover": {
              borderColor: (theme) => theme.palette.mockup.primary60,
            },
          }),
          ...(isCtaVisible && {
            "&:hover .group-card-cta, &:focus-visible .group-card-cta": {
              bgcolor: "mockup.primary99",
            },
          }),
        }}
      >
        {!readOnly && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={hideCheckbox ? "flex-end" : "space-between"}
          >
            {!hideCheckbox && (
              <Tooltip title={isCardTooltip ? "" : schoolWideTooltip}>
                <span onClick={stop}>
                  <Checkbox
                    checked={isSelected}
                    onChange={onToggleSelect}
                    onClick={stop}
                    disabled={isSchoolWide}
                    inputProps={{ "aria-label": group.name }}
                  />
                </span>
              </Tooltip>
            )}
            <Box onClick={stop}>
              <FabIconButton
                size="small"
                disabled={isSchoolWide}
                tooltipText={
                  isCardTooltip
                    ? ""
                    : isSchoolWide
                      ? schoolWideTooltip
                      : t("group.moreOptions")
                }
                aria-label={
                  isSchoolWide ? schoolWideTooltip : t("group.moreOptions")
                }
                aria-haspopup="true"
                aria-expanded={isMenuOpen}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                Icon={<IconThreeDots />}
              />
              <Menu
                anchorEl={menuAnchor}
                open={isMenuOpen}
                onClose={closeMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                MenuListProps={{ sx: { pt: 0, pb: 0 } }}
              >
                {!hideEditAction && (
                  <MenuItem
                    onClick={() => {
                      closeMenu()
                      onEdit?.()
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{
                        fontWeight: 500,
                        variant: "body2",
                      }}
                    >
                      {t("group.editGroup")}
                    </ListItemText>
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    closeMenu()
                    onDelete?.()
                  }}
                >
                  <ListItemText
                    primaryTypographyProps={{
                      fontWeight: 500,
                      variant: "body2",
                      color: "mockup.error50",
                    }}
                  >
                    {t("group.deleteGroup")}
                  </ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Stack>
        )}

        {readOnly && onViewMembers && (
          <Stack direction="row" justifyContent="flex-end">
            <Box onClick={stop}>
              <FabIconButton
                size="small"
                tooltipText={t("group.viewMembers")}
                onClick={onViewMembers}
                Icon={<IconEye />}
              />
            </Box>
          </Stack>
        )}

        <Stack
          onKeyDown={isClickable ? handleCardKeyDown : undefined}
          tabIndex={isClickable ? 0 : undefined}
          role={isClickable ? "button" : undefined}
          gap="16px"
          minWidth={0}
          overflow="hidden"
          sx={{ flexGrow: 1 }}
        >
          <Chip
            label={
              group.scope === "SCHOOL"
                ? t("group.filterSectionSchool")
                : t("group.filterSectionClass")
            }
            size="small"
            sx={{
              alignSelf: "flex-start",
              fontWeight: 500,
              ...groupScopeChipSx[group.scope],
            }}
          />
          <Tooltip title={isNameTruncated ? group.name : ""}>
            <Typography
              ref={nameRef}
              variant="subtitle1"
              color="mockup.neutral0"
              sx={{
                lineHeight: "132%",
                letterSpacing: "-0.09px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {group.name}
            </Typography>
          </Tooltip>

          <Stack
            minWidth={0}
            width="100%"
            gap="16px"
            overflow="hidden"
            sx={
              isEmpty
                ? {
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "24px",
                    padding: "14px",
                    flexGrow: 1,
                    flexShrink: 0,
                    bgcolor: "mockup.primary99",
                    borderRadius: "12px",
                  }
                : undefined
            }
          >
            {previewMembers.map((member) => (
              <Typography
                key={member.id}
                variant="body2"
                color="mockup.neutral10"
                noWrap
                minWidth={0}
              >
                {`${member.first_name ?? ""} ${member.last_name ?? ""}`.trim()}
              </Typography>
            ))}
            {remaining > 0 && (
              <Typography
                variant="body2"
                color="mockup.neutral40"
                fontWeight={600}
                noWrap
              >
                {t("group.moreMembers", { count: remaining })}
              </Typography>
            )}
            {isEmpty && (
              <Typography
                variant="body2"
                color="mockup.primary80"
                fontWeight={600}
                textAlign="center"
              >
                {t("group.noStudentsAdded")}
              </Typography>
            )}
          </Stack>
        </Stack>
        {isCtaVisible && (
          <Stack
            component="button"
            type="button"
            onClick={(e: MouseEvent) => {
              e.stopPropagation()
              onCardClick()
            }}
            className="group-card-cta"
            position="absolute"
            left={0}
            right={0}
            bottom={0}
            alignItems="center"
            py="14px"
            bgcolor="common.white"
            color="primary.main"
            border={0}
            borderTop={(theme) => `1px solid ${theme.palette.mockup.primary95}`}
            sx={{
              font: "inherit",
              cursor: "pointer",
              transition: "background-color 0.15s",
              "&:focus-visible": {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: "-2px",
              },
            }}
          >
            <Typography
              component="span"
              variant="body2"
              fontWeight={700}
              color="inherit"
            >
              {t("transaction.sendTransaction")}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Tooltip>
  )
}

export default GroupCard
